"use client";

import Link from "next/link";
import { PortableText } from "next-sanity";
import { IFooter } from "@/sanity/lib/definitions";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { resolveI18nRichtext, DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { useEffect, useState } from "react";

export default function OldFooter({
  data,
  language = DEFAULT_LANGUAGE,
}: {
  data?: IFooter;
  language?: string;
}) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    setLoaded(true);

    return () => {
      setLoaded(false);
    };
  }, []);

  if (!loaded || !data) return null;

  const policyColumn = data.navColumns?.[data.navColumns.length - 1];
  const disclaimer = resolveI18nRichtext(
    data.disclaimer,
    data.disclaimerI18n,
    language,
  );

  return (
    <footer className="w-full bg-white border-t border-black/[0.08] ">
      <div className="w-[min(229.25rem,100%)] mx-auto px-[4rem] lg:px-[5rem] py-[4rem] flex flex-col gap-[3rem]">
        {/* Copyright + nav links */}
        <div className="flex flex-col gap-[2rem] lg:flex-row lg:items-center lg:justify-between">
          <p className="mb-0 font-geist text-[1.75rem] leading-none text-black/70">
            © {new Date().getFullYear()} IQ Capital · All rights reserved
          </p>
          {policyColumn && (
            <div className="flex flex-wrap gap-x-[2rem] gap-y-[1rem]">
              {policyColumn.links.map((item, i) => {
                const href = resolvePageLink(item.link, language) ?? "#";
                const isExternal =
                  item.link?.externalLink && item.link?.openInNewTab;
                return (
                  <Link
                    key={i}
                    href={href}
                    target={isExternal ? "_blank" : "_self"}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    className="font-geist text-[1.75rem] leading-none text-black/70 hover:text-black/100 transition-colors duration-150"
                  >
                    {item.title}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Disclaimer */}
        {disclaimer && (
          <div className="richtext richtext-start flex flex-col gap-[1.5rem] font-geist  leading-[1.6] text-black/30 prose-p:mb-0 prose-p:text-black/70 prose-p:text-[1.75rem]">
            <PortableText value={disclaimer} components={richTextComponent} />
          </div>
        )}
      </div>
    </footer>
  );
}
